import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Pencil, Trash2 } from 'lucide-react';
import AlertModal from './AlertModal';
import CircularProgressBar from './circular-progress';
import RichTextEditor from './common/RichTextEditor';
import { normalizeRichTextValue } from '../helpers/global';
import { toDisplayableRichTextHtml, toRichTextEditorValue } from '../helpers/safeHtml';
import { highlightUtilitiesTagHtml, noteHasUtilitiesTag } from '../helpers/utilitiesNoteTag';
import useQuery from '../hooks/useQueryParams';

const Notes = ({
  notes = [],
  disabled,
  onNoteSubmit,
  text = '',
  onTextChange,
  bordered = true,
  style = {},
  onNoteEdit,
  onNoteDelete,
  currentUserName = ''
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmMeta, setDeleteConfirmMeta] = useState(null);
  const [highlightedNoteId, setHighlightedNoteId] = useState(null);
  const handledNoteId = useRef(null);
  const { noteId } = useQuery();

  useEffect(() => {
    if (!noteId || handledNoteId.current === noteId) return;
    const element = document.getElementById(`service-note-${noteId}`);
    if (!element) return;
    handledNoteId.current = noteId;
    setHighlightedNoteId(noteId);
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    setTimeout(() => setHighlightedNoteId(null), 1000);
  }, [noteId, notes]);

  const composerValue = normalizeRichTextValue(text);
  const editorDisabled = disabled || editingIndex != null || isDeleting || isAdding;

  const isCreator = (note, currentName) =>
    Boolean(
      currentName &&
        note?.userName &&
        String(note.userName)
          .trim()
          .toLowerCase() ===
          String(currentName)
            .trim()
            .toLowerCase()
    );

  const handleAddNote = async () => {
    if (disabled || !composerValue || !onNoteSubmit || isAdding) return;
    setIsAdding(true);
    try {
      await onNoteSubmit(composerValue);
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditStart = (index, note) => {
    setEditingIndex(index);
    setEditingText(toRichTextEditorValue(note.text || ''));
  };

  const handleEditSave = async () => {
    const nextValue = normalizeRichTextValue(editingText);
    if (editingIndex == null || !nextValue || !onNoteEdit) return;
    setIsSaving(true);
    try {
      await onNoteEdit(editingIndex, nextValue);
      setEditingIndex(null);
      setEditingText('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    if (isSaving) return;
    setEditingIndex(null);
    setEditingText('');
  };

  const handleDeleteClick = (index, note) => {
    if (!onNoteDelete) return;
    setDeleteConfirmMeta({ index, note });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmMeta || !onNoteDelete) return;
    const { index, note } = deleteConfirmMeta;
    setIsDeleting(true);
    try {
      await onNoteDelete(index, note);
      setDeleteConfirmMeta(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteDismiss = () => setDeleteConfirmMeta(null);

  const formatNoteTimestamp = note => {
    const when = moment(note['timestamp']).format('MMM D, YYYY · h:mm A');
    const wasEdited = note['editHistory'] && note['editHistory'].length > 0;
    return wasEdited ? `Edited · ${when}` : when;
  };

  return (
    <>
      <div className={` ${bordered ? 'card p-3' : ''} px-2 py-2 h-100`} style={style}>
        <h6 className="ml-1 largeFont my-2 font-weight-bold">Notes</h6>

        <RichTextEditor
          className="estoppel-notes-rich-text mt-2"
          value={text || ''}
          onChange={onTextChange}
          placeholder="Enter notes..."
          disabled={editorDisabled}
          enableLists
        />

        <div className="text-right">
          <Button
            disabled={editorDisabled || !composerValue}
            className="mt-2"
            size="sm"
            variant="success"
            onClick={handleAddNote}
          >
            {isAdding && <CircularProgressBar size={1} className="mr-2" />}
            Add Note
          </Button>
        </div>

        <hr className="my-2" />

        {notes && notes.length > 0 ? (
          notes.map((note, index) => {
            const canEditDelete = isCreator(note, currentUserName);
            const isEditing = editingIndex === index;
            const isHighlighted = highlightedNoteId != null && String(note['_id']) === String(highlightedNoteId);
            return (
              <div
                key={note['_id'] || index}
                id={note['_id'] ? `service-note-${note['_id']}` : undefined}
                className={`border rounded px-3 py-2 mt-2${isHighlighted ? '' : ' bg-white'}`}
                style={{
                  backgroundColor: isHighlighted ? 'var(--primary-light)' : '#fff',
                  transition: 'background-color 0.3s ease-in-out'
                }}
              >
                <div className="d-flex justify-content-between align-items-baseline">
                  <b className="mb-0 midFont">{note['userName']}</b>
                  <div className="d-flex align-items-center ml-2">
                    <i className="text-muted tinyFont mb-0">{formatNoteTimestamp(note)}</i>
                    {canEditDelete && !isEditing && (
                      <div className="ml-2 d-flex">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-1 text-secondary"
                          onClick={() => handleEditStart(index, note)}
                          title="Edit note"
                          disabled={isDeleting}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-1 text-danger"
                          onClick={() => handleDeleteClick(index, note)}
                          title="Delete note"
                          disabled={isDeleting}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="mt-2">
                    <RichTextEditor
                      className="estoppel-notes-rich-text"
                      value={editingText}
                      onChange={setEditingText}
                      placeholder="Enter notes..."
                      disabled={isSaving}
                      enableLists
                    />
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="success"
                        className="mr-1"
                        onClick={handleEditSave}
                        disabled={isSaving || !normalizeRichTextValue(editingText)}
                      >
                        {isSaving && <CircularProgressBar size={1} className="mr-2" />}
                        Save
                      </Button>
                      <Button size="sm" variant="secondary" onClick={handleEditCancel} disabled={isSaving}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-1 midFont instruction-content"
                    dangerouslySetInnerHTML={{
                      __html: noteHasUtilitiesTag(note['text'])
                        ? highlightUtilitiesTagHtml(toDisplayableRichTextHtml(note['text']))
                        : toDisplayableRichTextHtml(note['text'])
                    }}
                  />
                )}
              </div>
            );
          })
        ) : (
          <p className="text-secondary midFont my-1">{'No notes to show!'}</p>
        )}
      </div>

      <AlertModal
        show={deleteConfirmMeta != null}
        onHide={handleDeleteDismiss}
        onDismissClick={handleDeleteDismiss}
        onContinueClick={handleDeleteConfirm}
        dismissButtonText="Cancel"
        continueButtonText="Delete"
        continueButtonVariant="danger"
        alertText="Delete this note?"
        showProgress={isDeleting}
        progressText="Deleting note..."
      />
    </>
  );
};

export default Notes;
